/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import IconComponent from "@CubeComponents/icons";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian/index";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import DateTimePickerComponent from "@CubeComponents/Mui_Form/DateTimeComponent";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { DeleteIcon, PlusIcon } from "@Components/StickySidebarViewer/assets/selectionIcons";
import { getIconFromFieldType } from "./constants";
import { presetSectionsCodeObject } from "@Helpers/constants";
import {
  FormQuestionFields,
  addValidationToForms,
  formSectionType,
  useFormValidator,
  useForms,
  useFormPreviewLogic,
  getSectionName,
} from "@Features/forms";
import { useSelector } from "react-redux";
import _ from "lodash";
import { forwardRef, useEffect } from "react";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { Box } from "@mui/material";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import type { FormQuestionFieldsProps } from "@Features/forms/components/CreateInspectionAndProgress/questionFields";
import Text from "@CubeComponents/html/Text/Text";
import { RootState } from "@Reducers/index";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { v4 as uuid } from "uuid";

/** Declared as a type alias, not an interface, so it keeps the implicit index signature
 *  `SelectDropDown`'s `ActionMenuItem` requires. */
type Item = {
  id: string;
  text: string;
};
type ItemMap = Record<string, Item[]>;

/**
 * A question as this preview reads it — the widest shape its callers satisfy, so every member is
 * optional. Declared locally rather than as `TemplateQuestionField` because a dozen call sites
 * (mailbox, transmittals, assets, clash tests, contracts, form flow, inspection creation, the
 * template-preview popup) pass their own question objects, several of which carry neither `_id` nor
 * `fieldsVariableMapper` that that interface requires. `isVaries` is set by the multi-record
 * custom-fields panels and is modelled neither there nor by the server's `questionFieldSchema`.
 */
type PreviewQuestionField = {
  _id?: string;
  fieldType?: string;
  questionValue?: string;
  isMandatory?: boolean;
  fieldInstruction?: string;
  allowPreDefinedColValues?: boolean;
  isVaries?: boolean;
  [key: string]: unknown;
};

/**
 * A section as this preview reads it — again the widest shape its callers satisfy. The two table
 * flags are persisted by the builder (formSections.tsx) but missing from `TemplateSection`.
 */
type PreviewSection = {
  _id?: string;
  sectionCode?: string;
  sectionHeading?: string;
  isDefaultSection?: boolean;
  questionFields?: PreviewQuestionField[];
  tableDataSource?: TableSectionRow[];
  allowPreDefineRowValues?: boolean;
  allowNewRowAddInForm?: boolean;
  allowEditPreDefineRowInForm?: boolean;
  [key: string]: unknown;
};

/** Form template document being previewed. Extra keys (lookUpUsed, etc.) are preserved on save. */
type FormTemplateData = {
  template?: PreviewSection[];
  [key: string]: unknown;
};

type TableSectionRow = { rowId?: string | number };

/** The validator object this preview forwards; `useFormValidator` is the source of the shape. */
type FormValidator = ReturnType<typeof useFormValidator>;

/** Args of the parameters `useFormPreviewLogic` declares, so the two can never drift. */
type PreviewLogicProps = Parameters<typeof useFormPreviewLogic>[0];

interface FormPreviewProps {
  /**
   * The form/template being previewed. Left `unknown` at the boundary and narrowed to
   * {@link FormTemplateData} at each use below: the dozen call sites listed on
   * {@link PreviewQuestionField} pass `interface`-typed form objects (which get no implicit index
   * signature), objects whose `template` is `unknown[]`, and `null`, so no single named object type
   * accepts them all. Every assertion is erased at emit.
   */
  formData?: unknown;
  setFormData?: PreviewLogicProps["setFormData"];
  disabled?: boolean;
  allowPadding?: boolean;
  handleData?: PreviewLogicProps["handleData"];
  allowBorder?: boolean;
  headingFontSize?: string;
  defaultSection?: PreviewSection;
  showInGrid?: boolean;
  showInSidebar?: boolean;
  fullWidthInput?: boolean;
  removeIcons?: boolean;
  showAnswersOnly?: boolean;
  /**
   * Forwarded straight to `useFormPreviewLogic`. `unknown` at the boundary because the assets
   * sidebar's handler declares a narrower row than `CustomFieldSaveRow`, which `strictFunctionTypes`
   * rejects; asserted into the hook's parameter type at the call below.
   */
  handleSaveCustomFields?: unknown;
  setUpdateTemplateAfterUse?: PreviewLogicProps["setUpdateTemplateAfterUse"];
  formValidator?: FormValidator;
  showHeaderCheckBox?: boolean;
  /** Section `_id`s ticked in the header checkboxes (section-library / copy-sections pickers). */
  selectedSections?: string[];
  handleSectionSelection?: ((args: { isChecked: boolean; sectionId: string }) => void) | null;
  defaultExpanded?: boolean;
  spaceId?: string | null;
  optionsForQuestions?: ItemMap;
  defaultOptionsForQuestions?: Item[];
  optionSelectCallback?: (args: unknown, field: PreviewQuestionField) => void;
  templateSource?: string;
  resourceType?: string;
}
const FormPreview = forwardRef(
  ({
    formData = { template: [] },
    setFormData,
    disabled = true,
    allowPadding = true,
    handleData,
    allowBorder = true,
    headingFontSize = "text-xl",
    defaultSection,
    showInGrid = false,
    showInSidebar = false,
    fullWidthInput = false,
    removeIcons = false,
    showAnswersOnly = false,
    handleSaveCustomFields,
    setUpdateTemplateAfterUse,
    formValidator,
    showHeaderCheckBox = false,
    selectedSections = [],
    handleSectionSelection = null,
    defaultExpanded = true,
    spaceId = null,
    optionsForQuestions,
    defaultOptionsForQuestions,
    optionSelectCallback,
    templateSource = "FORM_FILLING",
    resourceType = "",
  }: FormPreviewProps) => {
    const { t } = useTranslation();
    const tempValidator = useFormValidator({});
    if (!formValidator) {
      formValidator = tempValidator;
    }
    const showFullInput = showInGrid || showInSidebar || fullWidthInput;
    const { userDetails } = useSelector((state: RootState) => state);
    /*
     * `handleInputChange`, `handleURLModalState` (from `useForms`) and `handleCustomFieldData` (from
     * `useFormPreviewLogic`) are each declared with a NARROWER parameter than the prop they are handed
     * to. `FormQuestionFieldsProps` is the published contract — `questionFields.tsx` deliberately
     * declares its own `QuestionFieldInput` / `SectionInput` because its three callers pass three
     * mutually unassignable question shapes (this file passes `PreviewQuestionField`, declared above as the
     * widest shape its own dozen callers satisfy) — and it therefore promises `eachField` may be
     * absent, `_id` may be absent, and `sectionIndex` may be `null`. The hooks require a whole
     * `QuestionField` with a required `_id`, and a non-null `sectionIndex`. Under
     * `strictFunctionTypes` that contravariance is an error at every one of the 12 attributes below.
     *
     * Nothing here can pass a narrower value than the hooks accept — this file always supplies both
     * `eachField` and a numeric `sectionIndex` — so each handler is asserted into the published prop
     * type at the attribute. The assertions are erased; the hooks and the component are both left
     * alone. The real fix is for `useForms` / `useFormPreviewLogic` to declare these payloads from
     * `FormQuestionFieldsProps` (or from the `*Args` types in `questionFieldsHelper.tsx`) instead of
     * from `QuestionField`, at which point these 12 assertions become no-ops and can be deleted.
     */
    const {
      handleInputChange,
      handleURLModalState,
      isFileUploading,
      handleFileUploadChange,
      removeUserFromList,
      saveEditedImage,
      handleRemoveUploadedFile,
      openAddUserModal,
      showUrlModal,
      handleSaveUrlData,
      showGeoLocationModal,
      setShowGeoLocationModal,
      handleGeoLocationModalState,
      closeUsersModal,
      showAddUsersModal,
      handleAddUserClick,
    } = useForms({
      uuid,
      userDetails,
    });

    // Use shared hook for form preview logic
    const { filterFields, handleCustomFieldData, handleAddTableRow, handleDeleteTableRow } = useFormPreviewLogic({
      // `PreviewSection` / `PreviewQuestionField` above are the widest shapes this file's callers
      // satisfy, which is deliberately looser than the hook's Zod-derived `FormTemplateField`.
      formData: formData as PreviewLogicProps["formData"],
      setFormData,
      handleData,
      handleSaveCustomFields: handleSaveCustomFields as PreviewLogicProps["handleSaveCustomFields"],
      setUpdateTemplateAfterUse,
      userDetails,
      resourceType,
      templateSource,
    });
    useEffect(() => {
      addValidationToForms({
        formValidator,
        template: ((formData as FormTemplateData)?.template ?? []) as Parameters<
          typeof addValidationToForms
        >[0]["template"],
        templateSource,
        resourceType,
      });
    }, []);

    const handleSectionSelect = ({
      args,
      sectionId,
    }: {
      args: { target: { checked: boolean } };
      sectionId: string;
    }) => {
      if (handleSectionSelection) {
        handleSectionSelection({ isChecked: args.target.checked, sectionId });
      }
    };

    const previewSectionHeader = (index: number, data: PreviewSection, isDefaultSection?: boolean) => {
      return (
        <div
          className={`flex justify-between w-full ${allowPadding ? "ps-4 pe-4" : ""} py-3 ${
            allowBorder ? "border-b border-button_light" : ""
          } items-center`}
        >
          <Box onClick={(e: React.SyntheticEvent) => e.stopPropagation()} className="flex gap-2 items-center">
            {showHeaderCheckBox && (
              <CheckBoxComponent
                onChange={(args) => handleSectionSelect({ args, sectionId: data._id! })}
                checked={selectedSections.includes(data._id!)}
              />
            )}
            <Text className={`${headingFontSize} font-semibold`}>{getSectionName(data)}</Text>
            {isDefaultSection && (
              <Text className="rounded-full text-xxs bg-gray-500 text-white px-2 py-0.5">{t("Default")}</Text>
            )}
          </Box>
          {/* <span>
            <IconComponent name="arrowDown" className="cursor-pointer" />
          </span> */}
        </div>
      );
    };

    const gridPreviewSectionContent = (data: PreviewSection, sectionIndex: number) => (
      <div className="grid grid-cols-2 gap-20 w-full px-2">
        {data.questionFields?.map(
          (fields: PreviewQuestionField, index: number) =>
            filterFields(fields as Parameters<typeof filterFields>[0]) && (
              <div key={fields._id} className="flex">
                <div className="flex">
                  <Text className="text-base font-semibold" data-testid="projectId">
                    {fields.questionValue}
                  </Text>
                  {fields.isMandatory && <Text className="text-2xl align-middle font-light text-red-500">*</Text>}
                </div>
                <div className="w-3/4 ms-auto">
                  <FormQuestionFields
                    formValidator={formValidator}
                    showAnswersOnly={showAnswersOnly}
                    templateSource={templateSource}
                    eachField={fields}
                    fieldIndex={index}
                    sectionIndex={sectionIndex}
                    section={data}
                    handleCustomFieldData={handleCustomFieldData as FormQuestionFieldsProps["handleCustomFieldData"]}
                    disabled={disabled}
                    width={showFullInput ? "100%" : "30%"}
                    formData={{ selectedForm: formData }}
                    handleInputChange={handleInputChange as FormQuestionFieldsProps["handleInputChange"]}
                    handleURLModalState={handleURLModalState as FormQuestionFieldsProps["handleURLModalState"]}
                    isFileUploading={isFileUploading}
                    handleFileUploadChange={handleFileUploadChange}
                    removeUserFromList={removeUserFromList}
                    saveEditedImage={saveEditedImage}
                    handleRemoveUploadedFile={handleRemoveUploadedFile}
                    openAddUserModal={openAddUserModal}
                    handleSaveUrlData={handleSaveUrlData}
                    showUrlModal={showUrlModal}
                    spaceId={spaceId!}
                    showGeoLocationModal={showGeoLocationModal}
                    setShowGeoLocationModal={setShowGeoLocationModal}
                    handleGeoLocationModalState={handleGeoLocationModalState}
                    closeUsersModal={closeUsersModal}
                    showAddUsersModal={showAddUsersModal}
                    handleAddUserClick={handleAddUserClick}
                  />
                </div>
              </div>
            ),
        )}
      </div>
    );

    const sidebarPreviewSectionContent = (data: PreviewSection, sectionIndex: number) => (
      <div className="flex flex-col gap-4 w-full px-2">
        {data.questionFields?.map(
          (fields: PreviewQuestionField, index: number) =>
            filterFields(fields as Parameters<typeof filterFields>[0]) && (
              <div key={fields._id} className="flex">
                <div className="flex gap-1 flex-1">
                  <Text
                    className="text-sm whitespace-normal text-ellipsis overflow-hidden font-semibold"
                    data-testid="projectId"
                  >
                    {fields.questionValue}
                  </Text>
                  {fields.isMandatory && <Text className="text-2xl align-middle font-light text-red-500">*</Text>}
                </div>
                <div className="flex-1">
                  <FormQuestionFields
                    formValidator={formValidator}
                    showAnswersOnly={showAnswersOnly}
                    templateSource={templateSource}
                    eachField={fields}
                    fieldIndex={index}
                    sectionIndex={sectionIndex}
                    section={data}
                    handleCustomFieldData={handleCustomFieldData as FormQuestionFieldsProps["handleCustomFieldData"]}
                    disabled={disabled}
                    width={showFullInput ? "100%" : "30%"}
                    formData={{ selectedForm: formData }}
                    handleInputChange={handleInputChange as FormQuestionFieldsProps["handleInputChange"]}
                    handleURLModalState={handleURLModalState as FormQuestionFieldsProps["handleURLModalState"]}
                    isFileUploading={isFileUploading}
                    handleFileUploadChange={handleFileUploadChange}
                    removeUserFromList={removeUserFromList}
                    saveEditedImage={saveEditedImage}
                    handleRemoveUploadedFile={handleRemoveUploadedFile}
                    openAddUserModal={openAddUserModal}
                    spaceId={spaceId!}
                    handleSaveUrlData={handleSaveUrlData}
                    showUrlModal={showUrlModal}
                    showGeoLocationModal={showGeoLocationModal}
                    setShowGeoLocationModal={setShowGeoLocationModal}
                    handleGeoLocationModalState={handleGeoLocationModalState}
                    closeUsersModal={closeUsersModal}
                    showAddUsersModal={showAddUsersModal}
                    handleAddUserClick={handleAddUserClick}
                  />
                </div>
              </div>
            ),
        )}
      </div>
    );

    const handleSelectOption = (args: unknown, field: PreviewQuestionField) => {
      if (optionSelectCallback) {
        optionSelectCallback(args, field);
      }
    };
    const defaultPreviewSectionContent = (data: PreviewSection, sectionIndex: number) => (
      <div className={`flex flex-col gap-8 ${allowPadding ? "ps-3" : ""}`}>
        {data.questionFields?.map(
          (fields: PreviewQuestionField, index: number) =>
            filterFields(fields as Parameters<typeof filterFields>[0]) && (
              <div key={fields._id} className="flex gap-3">
                <div className="flex h-full gap-3">
                  {!removeIcons && (
                    <span className="w-6 flex justify-start items-center flex-col gap-1">
                      <Text>{getIconFromFieldType(fields.fieldType!)}</Text>
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <span className="flex w-11/12 flex-col break-words gap-1">
                    <div className="flex w-full justify-between items-center">
                      <span className="flex">
                        <Text className="text-base font-semibold" data-testid="projectId">
                          {`${fields.questionValue}`}{" "}
                        </Text>
                        {fields.isMandatory && <Text className="text-2xl align-middle font-light text-red-500">*</Text>}
                      </span>

                      {(optionsForQuestions?.[fields?._id!] || defaultOptionsForQuestions?.length) && (
                        <SelectDropDown
                          select={(args) => handleSelectOption(args, fields)}
                          cssClass="e-caret-hide"
                          bgColor="transparent"
                          items={optionsForQuestions?.[fields?._id!] || defaultOptionsForQuestions}
                        >
                          <IconComponent name="threedots" width="1.25rem" height="1.25rem" className="cursor-pointer" />
                        </SelectDropDown>
                      )}
                    </div>
                    {fields.fieldInstruction && (
                      <span className="text-gray-400 w-full text-xs">
                        {fields.fieldInstruction ? `instructions : ${fields.fieldInstruction}` : ""}
                      </span>
                    )}
                  </span>
                  <div className="flex flex-col items-start w-full">
                    {fields.isVaries && <Text className="leading-none text-red-500 italic m-1">{t("Varies")}</Text>}
                    <FormQuestionFields
                      formValidator={formValidator}
                      showAnswersOnly={showAnswersOnly}
                      templateSource={templateSource}
                      eachField={fields}
                      fieldIndex={index}
                      sectionIndex={sectionIndex}
                      section={data}
                      handleCustomFieldData={handleCustomFieldData as FormQuestionFieldsProps["handleCustomFieldData"]}
                      disabled={disabled}
                      width={showFullInput ? "100%" : "30%"}
                      formData={{ selectedForm: formData }}
                      handleInputChange={handleInputChange as FormQuestionFieldsProps["handleInputChange"]}
                      handleURLModalState={handleURLModalState as FormQuestionFieldsProps["handleURLModalState"]}
                      isFileUploading={isFileUploading}
                      handleFileUploadChange={handleFileUploadChange}
                      removeUserFromList={removeUserFromList}
                      saveEditedImage={saveEditedImage}
                      handleRemoveUploadedFile={handleRemoveUploadedFile}
                      openAddUserModal={openAddUserModal}
                      handleSaveUrlData={handleSaveUrlData}
                      showUrlModal={showUrlModal}
                      spaceId={spaceId!}
                      showGeoLocationModal={showGeoLocationModal}
                      setShowGeoLocationModal={setShowGeoLocationModal}
                      handleGeoLocationModalState={handleGeoLocationModalState}
                      closeUsersModal={closeUsersModal}
                      showAddUsersModal={showAddUsersModal}
                      handleAddUserClick={handleAddUserClick}
                    />
                  </div>
                </div>
              </div>
            ),
        )}
      </div>
    );

    const getTableSectionGrid = (data: PreviewSection, sectionIndex: number) => {
      const dataSource = data.tableDataSource || [];
      return (
        <span className="overflow-auto flex flex-col gap-4 w-full">
          <span>
            {setFormData && (
              <ButtonComponent
                disabled={!data.allowPreDefineRowValues}
                onClick={() => handleAddTableRow({ sectionIndex })}
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              >
                <Text>{t("Add Row")}</Text>
              </ButtonComponent>
            )}
          </span>
          <table className="w-full overflow-auto flex flex-col h-full">
            <tr className="h-full w-full flex justify-normal text-start">
              {dataSource.length > 1 && (
                <Text className="p-4 text-sm border-2 w-14 bg-button_light border-button_light">.....</Text>
              )}
              <th className="p-4 text-sm border-2 w-14 bg-button_light border-button_light">
                <Text>{t("S. No.")}</Text>
              </th>
              {data.questionFields!.map((field: PreviewQuestionField) => (
                // <ToolTipComponent content={field.questionValue} position="top">
                <th
                  key={field._id}
                  className="p-4 items-center flex text-sm border-2 border-button_light w-full min-w-60 bg-button_light whitespace-nowrap overflow-hidden text-ellipsis"
                >
                  {field.isMandatory && <Text className="text-2xl font-light text-red-500">*</Text>}
                  <Text>{`${field.questionValue}`}</Text>
                </th>
                //  </ToolTipComponent>
              ))}
            </tr>
            {dataSource.map((obj: TableSectionRow, rowIndex: number) => (
              <tr key={obj.rowId} className="h-full w-full flex justify-normal text-start">
                {dataSource.length > 1 && (
                  <td className="p-4 min-w-14 max-w-40 border-2 border-button_light">
                    <IconComponent
                      onClick={() =>
                        handleDeleteTableRow({
                          sectionIndex,
                          rowIndex,
                        })
                      }
                      className="cursor-pointer"
                      name="delete"
                      width="1.25rem"
                      height="1.25rem"
                    />
                  </td>
                )}
                <td className="p-4 min-w-14 max-w-40 border-2 border-button_light">{rowIndex + 1}</td>
                {data.questionFields!.map((field: PreviewQuestionField, fieldIndex: number) => (
                  <td
                    key={field._id}
                    className="p-4 min-w-60 max-h-60 overflow-auto w-full border-2 border-button_light"
                  >
                    <FormQuestionFields
                      formValidator={formValidator}
                      showAnswersOnly={showAnswersOnly}
                      templateSource={templateSource}
                      eachField={field}
                      fieldIndex={fieldIndex}
                      sectionIndex={sectionIndex}
                      section={data}
                      handleCustomFieldData={handleCustomFieldData as FormQuestionFieldsProps["handleCustomFieldData"]}
                      disabled={!(field.allowPreDefinedColValues && !!setFormData && data.allowPreDefineRowValues)}
                      tableRowId={data.tableDataSource![rowIndex]?.rowId}
                      tableRowIndex={rowIndex}
                      width="100%"
                      source={formSectionType.TABLE_SECTION}
                      formData={{ selectedForm: formData }}
                      handleInputChange={handleInputChange as FormQuestionFieldsProps["handleInputChange"]}
                      handleURLModalState={handleURLModalState as FormQuestionFieldsProps["handleURLModalState"]}
                      isFileUploading={isFileUploading}
                      handleFileUploadChange={handleFileUploadChange}
                      removeUserFromList={removeUserFromList}
                      saveEditedImage={saveEditedImage}
                      handleRemoveUploadedFile={handleRemoveUploadedFile}
                      openAddUserModal={openAddUserModal}
                      handleSaveUrlData={handleSaveUrlData}
                      showUrlModal={showUrlModal}
                      spaceId={spaceId!}
                      showGeoLocationModal={showGeoLocationModal}
                      setShowGeoLocationModal={setShowGeoLocationModal}
                      handleGeoLocationModalState={handleGeoLocationModalState}
                      closeUsersModal={closeUsersModal}
                      showAddUsersModal={showAddUsersModal}
                      handleAddUserClick={handleAddUserClick}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </table>
        </span>
      );
    };

    const previewSectionContent = (sectionIndex: number, data: PreviewSection) => {
      if (
        [
          presetSectionsCodeObject.SECTION_TYPE_QUANTITY,
          presetSectionsCodeObject.SECTION_TYPE_FORM,
          presetSectionsCodeObject.SECTION_TYPE_REFERENCE,
          presetSectionsCodeObject.SECTION_TYPE_WBS_ITEMS,
          presetSectionsCodeObject.SECTION_TYPE_REVIEWS,
        ].includes(data.sectionCode)
      ) {
        return (
          <div className="flex items-center gap-2">
            <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} disabled={disabled}>
              <div className="flex items-center">
                <PlusIcon color="var(--system-colour-37" />
                <Text>{t("Add")}</Text>
              </div>
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} disabled={disabled}>
              <div className="flex items-center">
                <DeleteIcon color="var(--system-colour-37" />
                <Text>{t("Remove")}</Text>
              </div>
            </ButtonComponent>
          </div>
        );
      }
      if (data.sectionCode && data.sectionCode == presetSectionsCodeObject.SECTION_TYPE_TASK) {
        return (
          <div className="flex flex-col gap-5 p-3">
            <span className="flex gap-3 items-center">
              <Text>{t("Actual Start Date")}</Text>
              <DateTimePickerComponent
                name="actualStartDate"
                width="25rem"
                disabled
                id="actualStartDate"
                placeholder={t("Select Date")}
              />
            </span>
            <span className="flex gap-3 items-center">
              <Text>{t("Actual End Date")}</Text>
              <DateTimePickerComponent
                disabled
                name="actualEndDate"
                width="25rem"
                id="actualEndDate"
                placeholder={t("Select Date")}
              />
            </span>
            <span className="flex gap-3 items-center">
              <Text>{t("Progress %")}</Text>
              <NumericTextBoxComponent width="25rem" disabled />
            </span>
            <span className="flex gap-3 items-center">
              <Text>{t("Remaining Duration")}</Text>
              <NumericTextBoxComponent width="25rem" disabled />
            </span>
            <div className="flex items-center gap-2">
              <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} disabled>
                <div className="flex items-center">
                  <PlusIcon color="var(--system-colour-37)" />
                  <Text>{t("Add")}</Text>
                </div>
              </ButtonComponent>
              <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} disabled>
                <div className="flex items-center">
                  <DeleteIcon color="var(--system-colour-37)" />
                  <Text>{t("Remove")}</Text>
                </div>
              </ButtonComponent>
            </div>
          </div>
        );
      }

      if (data.sectionCode && data.sectionCode == presetSectionsCodeObject.SECTION_TYPE_MILESTONE) {
        return (
          <div className="flex flex-col gap-5">
            <div className="flex gap-5 p-3 items-center">
              <Text>{t("Milestone Completion Date")}</Text>
              <DateTimePickerComponent
                width="25rem"
                name="MilestoneCompletionDate"
                disabled
                id="MilestoneCompletionDate"
                placeholder={t("Select Date")}
              />
            </div>
            <div className="flex gap-3 items-center p-3">
              <Text>{t("Milestone Amount")}</Text>
              <NumericTextBoxComponent
                name="MilestoneAmount"
                width="25rem"
                disabled
                id="MilestoneAmount"
                placeholder={t("Enter Amount")}
              />
            </div>
          </div>
        );
      }
      if (data.sectionCode == presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION) {
        return getTableSectionGrid(data, sectionIndex);
      }
      if (showInGrid) {
        return gridPreviewSectionContent(data, sectionIndex);
      }
      if (showInSidebar) {
        return sidebarPreviewSectionContent(data, sectionIndex);
      }
      return defaultPreviewSectionContent(data, sectionIndex);
    };

    const getContent = () => {
      if (templateSource === "PROJECT_DASHBOARD") {
        const allQuestionsToDisplay: PreviewQuestionField[] = [];
        (formData as FormTemplateData)?.template?.forEach?.((section: PreviewSection) => {
          section.questionFields?.forEach((question: PreviewQuestionField) => {
            const answer = question.answer;
            allQuestionsToDisplay.push({
              ...question,
              answer: answer ?? {},
            });
          });
        });
        return (
          <div className="grid pt-4 gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {allQuestionsToDisplay.map((ques: PreviewQuestionField, index: number) => (
              <div key={ques._id} className="flex gap-2 items-center">
                <Text className="flex-1 font-semibold overflow-hidden text-ellipsis whitespace-nowrap">
                  {ques.questionValue}
                </Text>
                <div className="flex-2 overflow-hidden text-ellipsis whitespace-nowrap">
                  <FormQuestionFields
                    formValidator={formValidator}
                    showAnswersOnly={true}
                    templateSource={templateSource}
                    eachField={ques}
                    fieldIndex={index}
                    sectionIndex={0}
                    width={"100%"}
                    formData={{ selectedForm: formData }}
                    showUrlModal={showUrlModal}
                  />
                </div>
              </div>
            ))}
          </div>
        );
      }
      return (
        (formData as FormTemplateData)?.template?.length! > 0 &&
        (formData as FormTemplateData)?.template!.map((data: PreviewSection, index: number) => (
          <Accordion
            defaultExpanded={defaultExpanded}
            key={data._id}
            border={allowBorder ? "1px solid var(--primary-blue)" : ""}
            borderRadius="0.625rem"
            style={{ width: "100%" }}
            sx={
              !allowPadding && {
                "& .MuiAccordionDetails-root": {
                  height: "100%",
                  padding: "0",
                },
              }
            }
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon className="pe-2" />}>
              {previewSectionHeader(index, data, data.isDefaultSection)}
            </AccordionSummary>
            <AccordionDetails>{previewSectionContent(index, data)}</AccordionDetails>
          </Accordion>
        ))
      );
    };
    return (
      <div className="flex flex-col w-full h-full p-1 gap-2 overflow-auto" id="formData">
        {defaultSection && (
          <Accordion
            defaultExpanded={defaultExpanded}
            key="deafult-section-accordian"
            border={allowBorder ? "1px solid var(--primary-blue)" : ""}
            borderRadius="0.625rem"
            style={{ width: "100%" }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon className="pe-2" />}>
              {previewSectionHeader((formData as FormTemplateData).template?.length!, defaultSection, true)}
            </AccordionSummary>
            <AccordionDetails>
              {previewSectionContent((formData as FormTemplateData)?.template?.length!, defaultSection)}
            </AccordionDetails>
          </Accordion>
        )}
        {getContent()}
      </div>
    );
  },
);
FormPreview.displayName = "FormPreview";
export default FormPreview;
